import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, MapPin, Send, CheckCircle2, Target, Briefcase } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import useDocumentTitle from "../hooks/useDocumentTitle";

const PURPOSES = [
  { value: "general_inquiry", label: "General Inquiry" },
  { value: "support_bug", label: "Support / Bug Report" },
  { value: "hiring", label: "Hiring / Recruitment Help" },
  { value: "partnership", label: "Partnership / Sponsorship" },
  { value: "feedback", label: "Feedback / Suggestion" },
  { value: "billing", label: "Billing / Account" },
  { value: "other", label: "Other" },
];

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    location: "",
    purpose: "general_inquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  useDocumentTitle("Contact");

  // Pre-fill name/email when the auth context loads (only if user hasn't typed anything)
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.purpose || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#002FA7]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 bg-white text-xs font-medium text-zinc-700 mb-5">
            <Mail className="h-3 w-3 text-[#002FA7]" />
            <span className="tag-mono">talk to us</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-zinc-950">
            Get in touch with<br />the QaTestology team.
          </h1>
          <p className="mt-5 text-zinc-600 max-w-xl mx-auto">
            Have a question, found a bug, want to partner, or just want to say hi?
            Drop us a note — we read everything and reply within 1–2 business days.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <Card className="p-6 sm:p-8">
              {sent ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="h-12 w-12 rounded-full bg-emerald-50 grid place-items-center mx-auto text-emerald-700 mb-4">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950">Message sent!</h2>
                  <p className="text-sm text-zinc-600 mt-2 max-w-sm mx-auto">
                    A confirmation copy has been sent to <span className="font-mono">{form.email}</span>.
                    The team typically responds within 1–2 business days.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => { setSent(false); setForm((f)=>({ ...f, message: "" })); }}
                    data-testid="contact-send-another"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950">Send a message</h2>
                  <p className="text-sm text-zinc-500 -mt-3">Required fields are marked with *</p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-name">
                        Your name *
                        {user && <span className="ml-2 text-[10px] text-zinc-400 tag-mono uppercase">from your account</span>}
                      </Label>
                      <Input
                        id="contact-name"
                        required
                        value={form.name}
                        onChange={(e)=>update("name", e.target.value)}
                        data-testid="contact-name"
                        className={`mt-1.5 ${user ? "bg-zinc-100 cursor-not-allowed" : ""}`}
                        placeholder="Jane Tester"
                        readOnly={!!user}
                        aria-readonly={!!user}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email">
                        Email *
                        {user && <span className="ml-2 text-[10px] text-zinc-400 tag-mono uppercase">from your account</span>}
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e)=>update("email", e.target.value)}
                        data-testid="contact-email"
                        className={`mt-1.5 ${user ? "bg-zinc-100 cursor-not-allowed" : ""}`}
                        placeholder="jane@example.com"
                        readOnly={!!user}
                        aria-readonly={!!user}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-location">Location</Label>
                      <Input
                        id="contact-location"
                        value={form.location}
                        onChange={(e)=>update("location", e.target.value)}
                        data-testid="contact-location"
                        className="mt-1.5"
                        placeholder="Pune, India"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-purpose">Purpose *</Label>
                      <select
                        id="contact-purpose"
                        required
                        value={form.purpose}
                        onChange={(e)=>update("purpose", e.target.value)}
                        data-testid="contact-purpose"
                        className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                      >
                        {PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={(e)=>update("message", e.target.value)}
                      data-testid="contact-message"
                      className="mt-1.5"
                      placeholder="Tell us what's on your mind…"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {submitting ? "Sending..." : "Send message"}
                  </Button>
                </form>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-6 bg-zinc-950 text-white border-zinc-900">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-4 w-4 text-emerald-400" />
                <span className="text-xs uppercase tracking-widest font-mono text-emerald-400">prefer email?</span>
              </div>
              <p className="text-sm text-zinc-300 mb-4">
                Write to us directly. We respond from the same inbox where this form lands.
              </p>
              <a
                href="mailto:info@qatestology.com"
                data-testid="contact-direct-email"
                className="text-lg font-display font-bold hover:underline"
              >
                info@qatestology.com
              </a>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="h-4 w-4 text-[#002FA7]" />
                <span className="text-xs uppercase tracking-widest font-bold text-[#002FA7]">looking for jobs?</span>
              </div>
              <p className="text-sm text-zinc-600 mb-4">
                Browse open QA roles or run the AI Skill Gap analyzer — no contact needed.
              </p>
              <Link to="/jobs" data-testid="contact-jobs-link" className="text-sm font-semibold text-[#002FA7] hover:text-[#00227A]">
                Browse open jobs →
              </Link>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-[#002FA7]" />
                <span className="text-xs uppercase tracking-widest font-bold text-[#002FA7]">community</span>
              </div>
              <p className="text-sm text-zinc-600">
                Built in Pune, used by testers across India and the world.
                Have a meetup idea? Let us know.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-md bg-[#002FA7] text-white grid place-items-center">
              <Target className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-zinc-950">Qa<span className="text-[#002FA7]">Testology</span></span>
          </div>
          <div className="text-xs text-zinc-500 tag-mono">
            © {new Date().getFullYear()} qatestology.com · By the tester, to the tester, for the tester.
          </div>
        </div>
      </footer>
    </div>
  );
}
